import { useEffect, useRef, useState } from 'react';

// Inline WYSIWYG for email bodies.
//
// Built on contentEditable rather than pulling in a rich-text library: the
// entire feature set an outreach email needs is bold, italic, a link, and a
// list. A 200kB editor to get those would double the bundle and bring its own
// theming to fight with.
//
// TWO THINGS IT ALWAYS DOES, because both are how rich editors leak:
//
//   1. Paste is intercepted and inserted as PLAIN TEXT. Pasting from Word or a
//      browser otherwise drags in font tags, class names, and occasionally a
//      whole stylesheet — which renders unpredictably in Outlook and marks the
//      mail as machine-generated.
//   2. It emits BOTH html and a plain-text version on every change. The text
//      part is not a fallback nobody reads; it is the text/plain half of the
//      message, and cold mail without one lands worse.

export interface RichValue { html: string; text: string }

/** Strip markup to readable text — the text/plain half of the message. */
export function htmlToText(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  el.querySelectorAll('br').forEach((b) => b.replaceWith('\n'));
  el.querySelectorAll('p, div, li').forEach((b) => b.append('\n'));
  return (el.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

const CMDS = [
  { cmd: 'bold', label: 'B', title: 'Bold', style: { fontWeight: 800 } },
  { cmd: 'italic', label: 'I', title: 'Italic', style: { fontStyle: 'italic' } },
  { cmd: 'insertUnorderedList', label: '•', title: 'Bullet list', style: {} },
] as const;

export function RichText({ label, value, onChange, rows = 12, hint }: {
  label?: string;
  value: RichValue;
  onChange: (v: RichValue) => void;
  rows?: number;
  hint?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);

  // Only write into the DOM when the incoming value genuinely differs from
  // what is on screen. Assigning innerHTML on every render would move the
  // caret to the start on every keystroke.
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== value.html) el.innerHTML = value.html;
  }, [value.html]);

  const emit = () => {
    const html = ref.current?.innerHTML ?? '';
    onChange({ html, text: htmlToText(html) });
  };

  const run = (cmd: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  return (
    <div className="field">
      {label && <label className="fieldlabel">{label}</label>}
      <div className={`rt ${focused ? 'focused' : ''}`}>
        <div className="rt-tools">
          {CMDS.map((c) => (
            <button key={c.cmd} type="button" title={c.title}
              // onMouseDown, not onClick: a click would blur the editable area
              // first and the command would apply to nothing.
              onMouseDown={(e) => { e.preventDefault(); run(c.cmd); }}
              style={c.style as React.CSSProperties}>{c.label}</button>
          ))}
          <button type="button" title="Add a link"
            onMouseDown={(e) => {
              e.preventDefault();
              const url = window.prompt('Link to what URL?');
              if (!url) return;
              if (!/^https?:\/\//i.test(url)) {
                window.alert('Links must start with http:// or https://');
                return;
              }
              run('createLink', url);
            }}>Link</button>
          <button type="button" title="Remove formatting"
            onMouseDown={(e) => { e.preventDefault(); run('removeFormat'); }}>Clear</button>
        </div>
        <div
          ref={ref}
          className="rt-body"
          contentEditable
          suppressContentEditableWarning
          style={{ minHeight: `${rows * 1.6}em` }}
          onInput={emit}
          onBlur={() => { setFocused(false); emit(); }}
          onFocus={() => setFocused(true)}
          onPaste={(e) => {
            // See the note at the top: never let Word's markup in.
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            document.execCommand('insertText', false, text);
            emit();
          }}
        />
      </div>
      {hint && <p className="fieldhint">{hint}</p>}
    </div>
  );
}
